import React, { useState, useEffect, useRef } from 'react';
import katex from 'katex';
import { Sparkles, Check, Edit2, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { apiAIFetch } from '../../api';
import MathSymbolPalette from './MathSymbolPalette';
import MathTemplates from './MathTemplates';
import DrawMathCanvas from './DrawMathCanvas';
import 'katex/dist/katex.min.css';

const MathComponent = ({ expression, rawInput, isEditing, onUpdate }) => {
  const [localInput, setLocalInput] = useState(rawInput || '');
  const [loading, setLoading] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [showDraw, setShowDraw] = useState(false);
  const mathRef = useRef(null);
  const textareaRef = useRef(null);

  // Re-render KaTeX when expression changes
  useEffect(() => {
    if (mathRef.current && !isEditing) {
      try {
        const raw = expression || '...';
        // Auto-wrap multiple equations in \begin{gathered} unless already in an environment
        const latex = raw.includes('\\\\') && !raw.trimStart().startsWith('\\begin{')
          ? `\\begin{gathered}${raw}\\end{gathered}`
          : raw;
        katex.render(latex, mathRef.current, {
          throwOnError: false,
          displayMode: true,
        });
      } catch (e) {
        mathRef.current.innerText = 'Error parsing LaTeX';
      }
    }
  }, [expression, isEditing]);

  // Sync local input when rawInput prop changes
  useEffect(() => {
    setLocalInput(rawInput || '');
  }, [rawInput]);

  const handleMagicFormat = async () => {
    if (!localInput.trim()) return;

    setLoading(true);
    try {
      const response = await apiAIFetch('/api/format-latex', 'whiteboard.format_latex', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: localInput })
      });

      if (response.ok) {
        const data = await response.json();
        onUpdate({
          expression: data.latex || localInput,
          rawInput: localInput,
          isEditing: false
        });
      } else {
        // Fallback to raw input as LaTeX
        onUpdate({
          expression: localInput,
          rawInput: localInput,
          isEditing: false
        });
      }
    } catch (error) {
      console.error('Error formatting LaTeX:', error);
      // Fallback to raw input as LaTeX
      onUpdate({
        expression: localInput,
        rawInput: localInput,
        isEditing: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualSave = () => {
    // Assume input is already LaTeX
    onUpdate({
      expression: localInput,
      rawInput: localInput,
      isEditing: false
    });
  };

  const handleStartEditing = () => {
    onUpdate({ isEditing: true });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleManualSave();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      handleMagicFormat();
    }
  };

  // Insert symbol at cursor position
  const handleSymbolInsert = (latex, cursorOffset = 0) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setLocalInput(prev => prev + latex);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = localInput.substring(0, start);
    const after = localInput.substring(end);

    const newValue = before + latex + after;
    setLocalInput(newValue);

    // Set cursor position after React re-render
    setTimeout(() => {
      const newPos = start + latex.length + cursorOffset;
      textarea.selectionStart = newPos;
      textarea.selectionEnd = newPos;
      textarea.focus();
    }, 0);
  };

  const handleAddLine = () => handleSymbolInsert(' \\\\ ', 0);

  // Insert template as a new equation line rather than replacing existing content
  const handleTemplateSelect = (latex) => {
    setLocalInput(prev => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed} \\\\ ${latex}` : latex;
    });
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  if (isEditing) {
    return (
      <div className="wb-math wb-math-editing">
        <label className="wb-math-label">Math Input</label>
        <textarea
          ref={textareaRef}
          className="wb-math-input"
          value={localInput}
          onChange={(e) => setLocalInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type math here (e.g. 'integral of x squared' or 'x^2 + y^2 = r^2')"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          autoFocus
        />
        <div className="wb-math-actions">
          <button
            onClick={handleMagicFormat}
            disabled={loading}
            className="wb-magic-button"
          >
            {loading ? (
              <span className="wb-spinner" />
            ) : (
              <>
                <Sparkles size={14} />
                Magic Format
              </>
            )}
          </button>
          <MathTemplates onSelect={handleTemplateSelect} />
          <button
            onClick={handleAddLine}
            className="wb-templates-btn"
            title="Add equation line"
          >
            + Line
          </button>
          <button
            onClick={() => { setShowPalette(!showPalette); setShowDraw(false); }}
            className="wb-templates-btn"
            title="Symbol Palette"
          >
            {showPalette ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Symbols
          </button>
          <button
            onClick={() => { setShowDraw(!showDraw); setShowPalette(false); }}
            className={`wb-templates-btn${showDraw ? ' active' : ''}`}
            title="Draw or type equation"
          >
            <Pencil size={14} />
            Draw
          </button>
          <button
            onClick={handleManualSave}
            className="wb-save-button"
            title="Save as LaTeX (Esc)"
          >
            <Check size={16} />
          </button>
        </div>
        {showPalette && (
          <MathSymbolPalette
            onInsert={handleSymbolInsert}
            textareaRef={textareaRef}
          />
        )}
        {showDraw && (
          <DrawMathCanvas
            onInsert={(text) => {
              setLocalInput((prev) => {
                const trimmed = prev.trim();
                return trimmed ? `${trimmed} \\\\ ${text}` : text;
              });
              setShowDraw(false);
              if (textareaRef.current) textareaRef.current.focus();
            }}
            onClose={() => setShowDraw(false)}
          />
        )}
        <div className="wb-math-hint">
          Ctrl+Enter for Magic Format • Escape to save • + Line to add equations
        </div>
      </div>
    );
  }

  return (
    <div
      className="wb-math wb-math-display"
      onDoubleClick={(e) => {
        e.stopPropagation();
        handleStartEditing();
      }}
    >
      <div ref={mathRef} className="wb-math-rendered" />
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleStartEditing();
        }}
        className="wb-edit-button"
        title="Edit"
      >
        <Edit2 size={14} />
      </button>
    </div>
  );
};

export default React.memo(MathComponent);
